<template>
  <div>
    <panel class="document-generate" title="Generowanie dokumentacji">
      <div v-if="!hideSelect" class="form-group">
        <autocomplete
          v-model="selectedView"
          title="Wybierz szablon"
          value-type="number"
          :options="{ values: filteredViews, labelField: 'name', valueField: 'id' }"
        />
      </div>

      <h2 v-else>{{ view.name }}</h2>

      <div class="document-generate-row">
        <schema-form
          v-if="loadedSchema && schema"
          :template-name="$schema.$title"
          :schema="$schema"
          :external="external"
        />

        <div v-if="loadedSchema && attachments.length" class="form-group mt-4">
          <p>Dołącz do paczki:</p>

          <ul class="remove-dot">
            <li v-for="(attachment, index) in attachments" :key="'attachement_' + index" class="mt-2">
              <w-checkbox
                :title="attachment.name"
                :value="attachment.id"
                :model="$schema.attachmentIds"
                @input="clearAttachmentsTable"
              />
            </li>
          </ul>
        </div>

        <div v-if="loadedSchema && attachments.length && $schema.attachmentsTable" class="form-group mt-4">
          <w-wysiwyg :model="$schema.attachmentsTable" :config="attachmentsTableConfig" />
        </div>
      </div>

      <div slot="footer">
        <w-button
          icon="fal fa-eye mr-2"
          class="mr-2 btn-info"
          text="Podgląd"
          :disabled="postFormsDisabled"
          @click="openPreview(schema && $schema.$getData())"
        />

        <post-form
          class="mr-2"
          icon="fal fa-cloud-download mr-2"
          button-class="btn-info"
          :text="attachmentsLength > 0 ? 'Pobierz zip' : 'Pobierz pdf'"
          :disabled="postFormsDisabled"
          :fields="viewFormFields"
          :url="attachmentsLength > 0 ? zipUrl : pdfUrl"
        />

        <w-button class="btn-warning mr-2" text="Edytuj treść" :disabled="postFormsDisabled" @click="editHTML" />

        <system-message :name="messageName" :entity-type="entityType" :entity-id="documentId.toString()" />

        <div class="float-right">
          <w-button class="btn-primary mr-2" text="Zapisz dokument" @click="saveAsAttachment" />

          <w-button
            v-if="documentIndex.systemNumbers && documentIndex.systemNumbers.mdokId"
            class="btn-primary"
            text="Zapisz i prześlij do MDOK"
            @click="sendToMDOK(view)"
          />
        </div>
      </div>

      <preview-modal
        :show-edit-button="true"
        :show="showPreviewModal"
        :html="previewHTML"
        @close="closePreviewModal"
        @editHTML="editHTML"
      />
    </panel>

    <router-link :to="{ name: 'documentView', query: $route.query }">
      <w-button icon="fal fa-arrow-left" class="btn-light" text="Wróć" />
    </router-link>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Autocomplete from '@/components/formComponents/NoSchemaAutocomplete.vue';
import SystemMessage from '@/components/formComponents/SystemMessage';
import SchemaForm from '@/components/forms/SchemaForm';
import PreviewModal from '@/components/modals/PreviewModal';
import Panel from '@/components/panel/Panel';
import PostForm from '@/components/postForm/PostForm';
import { EntityType } from '@/enums/entityType.enum';
import { Errors } from '@/enums/errors.enum';
import { Successes } from '@/enums/successes.enum';
import { TableName } from '@/enums/tableName';
import { formMixin } from '@/mixins/form';
import { generateMixin } from '@/mixins/generate';
import { notificationsMixin } from '@/mixins/notifications';
import { previewMixin } from '@/mixins/preview';
import { renderMixin } from '@/mixins/render';
import schema from '@/schemas/generateDocument.schema';
import { tryParseInt } from '@/utils/number';

// @vuese
// Generowanie załączników
// @group Document
export default {
  name: 'DocumentGenerate',
  components: { Autocomplete, SchemaForm, Panel, PostForm, SystemMessage, PreviewModal },
  mixins: [formMixin, generateMixin, renderMixin, previewMixin, notificationsMixin],
  schema: { type: 'object' },
  props: {
    hideSelect: { type: Boolean, default: false }
  },
  data() {
    return {
      attachmentsTableConfig: { allowedContent: true },
      selectedView: parseInt(this.$route.params.viewId) || parseInt(this.$route.query.viewId),
      entityType: TableName.document
    };
  },
  computed: {
    ...mapState({
      // Dokument z indeksu
      documentIndex: state => state.documentIndex.data,

      // Lista załączników
      attachments: state => state.document.attachment.list,

      // Lista ustawień
      settings(state) {
        if (this.view?.printOptions?.settings?.length) {
          return this.view.printOptions.settings.reduce((settingsAccumulator, code) => {
            settingsAccumulator[code] = state.setting.settings[code];
            return settingsAccumulator;
          }, {});
        }
        return {};
      }
    }),

    filteredViews() {
      return this.views.filter(view => !view.data?.hideInSingleDocument);
    },

    // Identyfikator dokumentu
    documentId() {
      return this.$route.params.documentId;
    },

    // Aktualnie wybrany widok
    view() {
      if (this.selectedView) {
        return this.filteredViews.find(view => view.id === this.selectedView) || this.filteredViews[0];
      }
      return this.filteredViews[0];
    },

    // Tytuł powiadomienia
    messageName() {
      return `Przypomnienie o akcie prawnym: "${this.view.name}"`;
    },

    // Adres podglądu wygenerowanego załącznika
    previewUrl() {
      return this.getPreviewURL(this.documentId, this.view.id);
    },

    // Adres do pobrania PDF z wygenerowanym załącznikiem
    pdfUrl() {
      return this.getPdfURL(this.documentId, this.view.id);
    },

    // Adres do pobrania paczki zip z wygenerowanymi załącznikami
    zipUrl() {
      return this.getZipURL(this.documentId, this.view.id);
    },

    // Określa wielkość tablicy załączników
    attachmentsLength() {
      if (!this.loadedSchema) {
        return 0;
      }
      return this.$schema.attachmentIds.$model.length;
    },

    // Dane zewnętrzne do schemy
    external() {
      return { document: this.documentIndex, attachments: this.attachments, settings: this.settings };
    }
  },

  watch: {
    // Ustawia query odpowiednie dla wybranego widoku
    selectedView(viewId) {
      this.$router.replace({ name: this.$route.name, query: { ...this.$route.query, viewId } });
    },

    // Przeładowuje schemę po zmianie wybranego widoku
    view(view) {
      this.loadedSchema = false;
      this.loadSchema();
    }
  },
  mounted() {
    if (!this.selectedView || this.filteredViews.every(view => view.id !== this.selectedView)) {
      this.selectedView = this.filteredViews[0].id;
    }
  },
  methods: {
    // @vuese
    // Zapisuje jako załącznik dokumentu
    saveAsAttachment() {
      this.save('view/saveAsAttachment');
    },

    // @vuese
    // Przesyła załącznik do MDOK
    sendToMDOK() {
      this.save('view/sendToMDOK');
    },

    // @vuese
    // Zapis załącznika
    save(action) {
      this.$store
        .dispatch(action, {
          viewId: this.view.id,
          documentId: tryParseInt(this.documentId),
          data: this.schema && this.$schema.$getData()
        })
        .then(() => this.notifySuccess(Successes.ATTACHMENT_SAVED))
        .then(() => this.getDocumentAttachments())
        .catch(err => this.notifyError(Errors.ERROR_TRY_AGAIN_LATER));
    },

    // @vuese
    // Edycja HTML'a
    editHTML() {
      this.$store.commit('view/setPreviewData', this.schema && this.$schema.$getData());
      this.$router.push({
        name: 'editViewHtml',
        params: { viewId: this.view.id }
      });
    },

    // @vuese
    // Ładuje schemę
    loadSchema() {
      this.getViewSettings().then(() => {
        this.model = {};
        this.$options.external = this.external;
        if (this.schema) {
          this.schema.properties.attachmentIds = schema.properties.attachmentIds;
          this.schema.properties.attachmentNames = schema.properties.attachmentNames;
          this.$setSchema(this.schema);
        } else {
          this.$setSchema(schema);
        }
        this.loadedSchema = true;
      });
    },

    // @vuese
    // Pobiera ustawienia widoku
    getViewSettings() {
      if (this.view?.printOptions?.settings?.length) {
        return this.$store
          .dispatch('setting/publicList', { codes: this.view.printOptions.settings, order: 'id', desc: true })
          .then(settings => {
            settings.forEach((setting, index) => {
              const isProcedureSetting = settings.filter(s => s.code === setting.code).length > 1;
              const isFirstTime = settings.findIndex(s => s.code === setting.code) === index;
              if (isProcedureSetting && isFirstTime) {
                const correctSetting = settings.find(
                  s => s.code === setting.code && s.entityId.toString() === this.$route.params.procedureId.toString()
                );
                this.$store.commit('setting/saveByCode', correctSetting);
              }
            });
          });
      }
      return Promise.resolve(undefined);
    },

    // @vuese
    // Pobierz listę załączników dokumentu
    getDocumentAttachments() {
      return this.$store.dispatch('document/attachment/list', {
        order: 'order',
        desc: false,
        count: -1,
        entityType: EntityType.DOCUMENT,
        entityId: this.$route.params.documentId.toString()
      });
    },

    // @vuese
    // Czyści tabelę z załącznikami
    clearAttachmentsTable() {
      if (this.$schema.attachmentsTable) {
        this.$nextTick(() => this.$schema.attachmentsTable.$setDefault());
      }
    }
  }
};
</script>

<style>
.document-generate .document-generate-row .btn {
  font-weight: 700;
  text-transform: uppercase;
}
.document-generate .document-generate-row .modal .btn {
  font-weight: none;
  text-transform: none;
}
.remove-dot {
  list-style-type: none;
}
</style>
